import { EmailItButton, TextItButton } from "../components/email-dialog";
import {
  type ComplianceDetail,
  type ComplianceDoc,
  type ContactListItem,
  type DocState,
  type FileItem,
  type LaborItem,
  WORK_ORDER_STATUS_LABELS,
  type WorkOrderDetail,
  type WorkOrderStatus,
  formatQuantity,
  parseQuantity,
  priceLines,
} from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { ApiError, del, errorMessage, get, patch, post, put } from "../api";
import { FilesPanel } from "../components/files";
import { fromLocalInput, toLocalInput } from "../components/schedule";
import { Alert, Badge, Button, Checkbox, Dialog, Empty, Field, Loading, PageHeader, Panel, PanelHeader, SelectField, Tabs, TextAreaField, cx, formatCents, formatDate, formatDateTime } from "../components/ui";
import { useSession } from "../session";
import { RemindDialog } from "../components/insurance";
import { useRememberedTab } from "../components/use-tab";
import { RichText } from "../components/rich-text";

const TONES: Record<WorkOrderStatus, "ok" | "wait" | "off" | "brand"> = {
  draft: "wait",
  sent: "brand",
  confirmed: "ok",
  declined: "off",
  proposed: "wait",
  completed: "ok",
  canceled: "off",
};

export function WorkOrderStatusBadge({ w }: { w: Pick<WorkOrderDetail, "status" | "noResponse"> }) {
  return (
    <span className="inline-flex gap-1">
      <Badge tone={TONES[w.status]}>{WORK_ORDER_STATUS_LABELS[w.status]}</Badge>
      {w.noResponse && <Badge tone="brand">No answer yet</Badge>}
    </span>
  );
}

function when(w: Pick<WorkOrderDetail, "startsAt" | "endsAt">) {
  if (!w.startsAt) return "Not scheduled";
  return w.endsAt ? `${formatDateTime(w.startsAt)} – ${formatDateTime(w.endsAt)}` : formatDateTime(w.startsAt);
}

function WorkOrderRows({ rows, showJob, onOpen }: { rows: WorkOrderDetail[]; showJob?: boolean; onOpen: (w: WorkOrderDetail) => void }) {
  return (
    <ul>
      {rows.map((w) => (
        <li key={w.id} className="border-b border-mist last:border-0">
          <button onClick={() => onOpen(w)} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-5 py-3 text-left hover:bg-mist/50">
            <span className="font-semibold">WO {w.number}</span>
            <WorkOrderStatusBadge w={w} />
            <span className="min-w-0 flex-1 truncate text-sm">
              {[w.sub?.name ?? "No sub yet", w.title, showJob ? w.jobLabel : null].filter(Boolean).join(" · ")}
            </span>
            <span className="text-sm text-muted">{when(w)}</span>
            {w.totalCents !== null && w.totalCents > 0 && <span className="text-sm font-semibold">{formatCents(w.totalCents)}</span>}
          </button>
        </li>
      ))}
    </ul>
  );
}

/** The job's Work Orders tab. */
export function JobWorkOrders({ jobId }: { jobId: string }) {
  const { can } = useSession();
  const list = useQuery({ queryKey: ["work-orders", "job", jobId], queryFn: () => get<WorkOrderDetail[]>(`/api/jobs/${jobId}/work-orders`) });
  const [editing, setEditing] = useState<WorkOrderDetail | "new" | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  // A link to one work order (?workOrder=) opens it; ?editWorkOrder= (just made from a material order) opens it to edit.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const show = params.get("workOrder");
    const edit = params.get("editWorkOrder");
    if (!list.data || (!show && !edit)) return;
    const w = list.data.find((x) => x.id === (edit ?? show));
    if (w) {
      if (edit) setEditing(w);
      else setOpen(w.id);
    }
    const next = new URLSearchParams(params);
    next.delete("workOrder");
    next.delete("editWorkOrder");
    setParams(next, { replace: true });
  }, [list.data, params, setParams]);
  return (
    <Panel>
      <PanelHeader
        title="Work orders"
        description="Scope, materials, and labor for a sub or crew. They get a link to confirm, decline, or propose a new time."
        action={
          can("workorders.edit") && (
            <Button variant="primary" className="px-3 py-1.5" onClick={() => setEditing("new")}>
              New work order
            </Button>
          )
        }
      />
      {list.isLoading ? (
        <div className="px-5">
          <Loading />
        </div>
      ) : !list.data?.length ? (
        <Empty>No work orders yet.</Empty>
      ) : (
        <WorkOrderRows rows={list.data} onOpen={(w) => setOpen(w.id)} />
      )}
      {editing && (
        <WorkOrderEditor
          jobId={jobId}
          wo={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(w) => {
            setEditing(null);
            setOpen(w.id);
          }}
        />
      )}
      {open && <WorkOrderDialog id={open} onClose={() => setOpen(null)} onEdit={(w) => {
        setOpen(null);
        setEditing(w);
      }} />}
    </Panel>
  );
}

/** Every work order, open ones first, plus subcontractors whose paperwork needs chasing. */
export function WorkOrdersPage() {
  const [tab, setTab] = useRememberedTab<"open" | "all" | "paperwork">("work-orders", ["open", "all", "paperwork"], "open");
  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditing] = useState<WorkOrderDetail | null>(null);
  const list = useQuery({ queryKey: ["work-orders", "all", tab], queryFn: () => get<WorkOrderDetail[]>(`/api/work-orders?status=${tab === "all" ? "all" : "open"}`), enabled: tab !== "paperwork" });
  const paperwork = useQuery({
    queryKey: ["compliance"],
    queryFn: () => get<{ contactId: string; name: string; phone: string; status: ComplianceDetail["status"]; wcExpiresOn: string | null; glExpiresOn: string | null }[]>("/api/compliance"),
  });
  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Work orders" description="Add work orders from a job's Work Orders tab." />
      <Tabs
        tabs={[
          { value: "open", label: "Open" },
          { value: "all", label: "All" },
          { value: "paperwork", label: `Sub paperwork${paperwork.data?.length ? ` (${paperwork.data.length})` : ""}` },
        ]}
        value={tab}
        onChange={setTab}
      />
      {tab === "paperwork" ? (
        <Panel>
          <PanelHeader title="Subcontractor paperwork" description="Subs missing a W-9, workers' comp, or general liability, or with one expiring soon." />
          {paperwork.isLoading ? (
            <div className="px-5">
              <Loading />
            </div>
          ) : !paperwork.data?.length ? (
            <Empty>Every subcontractor's paperwork is current.</Empty>
          ) : (
            <ul>
              {paperwork.data.map((s) => (
                <li key={s.contactId} className="border-b border-mist last:border-0">
                  <Link to={`/contacts/${s.contactId}`} className="flex flex-wrap items-center gap-3 px-5 py-3 hover:bg-mist/50">
                    <span className="font-semibold">{s.name}</span>
                    <span className="text-sm text-ink-soft">
                      {s.status.problems.length
                        ? s.status.problems.join(" · ")
                        : [s.status.workersComp === "expiring" && `Workers' comp expires ${formatDate(s.wcExpiresOn)}`, s.status.generalLiability === "expiring" && `GL expires ${formatDate(s.glExpiresOn)}`].filter(Boolean).join(" · ")}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      ) : (
        <Panel>
          {list.isLoading ? (
            <div className="px-5">
              <Loading />
            </div>
          ) : !list.data?.length ? (
            <Empty>{tab === "open" ? "No open work orders." : "No work orders yet."}</Empty>
          ) : (
            <WorkOrderRows rows={list.data} showJob onOpen={(w) => setOpen(w.id)} />
          )}
        </Panel>
      )}
      {open && (
        <WorkOrderDialog
          id={open}
          onClose={() => setOpen(null)}
          onEdit={(w) => {
            setOpen(null);
            setEditing(w);
          }}
        />
      )}
      {editing && (
        <WorkOrderEditor
          jobId={editing.jobId}
          wo={editing}
          onClose={() => setEditing(null)}
          onSaved={(w) => {
            setEditing(null);
            setOpen(w.id);
          }}
        />
      )}
    </div>
  );
}

type LineForm = { laborItemId: string | null; description: string; unit: string; quantity: string; price: string };

function useSubs() {
  return useQuery({ queryKey: ["contacts", "subs"], queryFn: () => get<{ items: ContactListItem[] }>("/api/contacts?category=subcontractor&active=1&limit=200").then((r) => r.items) });
}

function useLaborItems() {
  return useQuery({ queryKey: ["labor-items"], queryFn: () => get<LaborItem[]>("/api/labor-items") });
}

const dollars = (cents: number) => (cents / 100).toFixed(2);

function WorkOrderEditor({ jobId, wo, onClose, onSaved }: { jobId: string; wo: WorkOrderDetail | null; onClose: () => void; onSaved: (w: WorkOrderDetail) => void }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const money = can("money.view");
  const subs = useSubs();
  const labor = useLaborItems();
  const [subId, setSubId] = useState(wo?.sub?.id ?? "");
  const [title, setTitle] = useState(wo?.title ?? "");
  const [scope, setScope] = useState(wo?.scope ?? "");
  const [materials, setMaterials] = useState(wo?.materials ?? "");
  const [startsAt, setStartsAt] = useState(toLocalInput(wo?.startsAt ?? null));
  const [endsAt, setEndsAt] = useState(toLocalInput(wo?.endsAt ?? null));
  const [lines, setLines] = useState<LineForm[]>(
    (wo?.lines ?? []).map((l) => ({ laborItemId: l.laborItemId, description: l.description, unit: l.unit, quantity: formatQuantity(l.quantity), price: dollars(l.unitCents) })),
  );
  // The sub's own rates, for lines added from the price list.
  const rates = useQuery({ queryKey: ["labor-rates", subId], queryFn: () => get<Record<string, number>>(`/api/contacts/${subId}/labor-rates`), enabled: money && !!subId });
  const fill = useMutation({
    mutationFn: () => get<{ lines: { laborItemId: string | null; description: string; unit: string; quantity: number; unitCents: number }[]; from: { fileName: string; wastePercent: number } }>(`/api/jobs/${jobId}/labor-suggestion${subId ? `?subId=${subId}` : ""}`),
    onSuccess: (r) => {
      const fresh = r.lines.map((l) => ({ laborItemId: l.laborItemId, description: l.description, unit: l.unit, quantity: formatQuantity(l.quantity), price: dollars(l.unitCents) }));
      // Counted lines replace the same items; anything typed in stays.
      setLines([...fresh, ...lines.filter((l) => !l.laborItemId || !fresh.some((f) => f.laborItemId === l.laborItemId))]);
    },
  });

  const parsed = lines.map((l) => ({ laborItemId: l.laborItemId, description: l.description.trim(), unit: l.unit.trim(), quantity: parseQuantity(l.quantity), unitCents: /^\d+(\.\d{1,2})?$/.test(l.price.trim()) ? Math.round(Number(l.price.trim()) * 100) : null }));
  const bad = parsed.findIndex((l) => l.quantity === null || l.unitCents === null || !l.description);
  const priced = bad === -1 ? priceLines(parsed.map((l) => ({ ...l, quantity: l.quantity!, unitCents: l.unitCents! }))) : null;
  const setLine = (i: number, change: Partial<LineForm>) => setLines(lines.map((l, k) => (k === i ? { ...l, ...change } : l)));

  const save = useMutation({
    mutationFn: () => {
      const body = {
        subId: subId || null,
        title,
        scope,
        materials,
        startsAt: fromLocalInput(startsAt),
        endsAt: fromLocalInput(endsAt),
        ...(money ? { lines: priced!.lines.map(({ totalCents: _t, ...l }) => l) } : {}),
      };
      return wo ? patch<WorkOrderDetail>(`/api/work-orders/${wo.id}`, body) : post<WorkOrderDetail>(`/api/jobs/${jobId}/work-orders`, body);
    },
    onSuccess: async (w) => {
      await qc.invalidateQueries({ queryKey: ["work-orders"] });
      onSaved(w);
    },
  });
  const goesBackToDraft = wo && wo.status !== "draft";

  return (
    <Dialog
      wide
      title={wo ? `Edit work order ${wo.number}` : "New work order"}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} disabled={money && bad !== -1} onClick={() => save.mutate()}>
            {wo ? "Save" : "Add work order"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        {goesBackToDraft && <Alert tone="info">This work order has been sent. Saving changes takes it back to a draft and its link stops working; send it again so the sub can confirm what changed.</Alert>}
        <SelectField label="Subcontractor or crew" value={subId} onChange={(e) => setSubId(e.target.value)} hint={subs.data && !subs.data.length ? "Add a contact with a subcontractor type first." : undefined}>
          <option value="">Pick later</option>
          {subs.data?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.displayName}
            </option>
          ))}
        </SelectField>
        <Field label="Title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Roof replacement" />
        <TextAreaField label="Scope of work" value={scope} onChange={(e) => setScope(e.target.value)} rows={5} />
        <TextAreaField label="Materials" value={materials} onChange={(e) => setMaterials(e.target.value)} rows={3} hint="What's being delivered or what they bring." />
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Starts (optional)" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
          <Field label="Ends (optional)" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
        </div>
        {money && (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-semibold text-ink-soft">Labor pricing (optional)</legend>
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-[1fr_70px_80px_90px_auto] items-end gap-2">
                <Field label={i === 0 ? "Description" : ""} aria-label="Description" value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} />
                <Field label={i === 0 ? "Qty" : ""} aria-label="Quantity" inputMode="decimal" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} error={parsed[i]!.quantity === null ? " " : undefined} />
                <Field label={i === 0 ? "Unit" : ""} aria-label="Unit" value={l.unit} onChange={(e) => setLine(i, { unit: e.target.value })} />
                <Field label={i === 0 ? "$ each" : ""} aria-label="Price each" inputMode="decimal" value={l.price} onChange={(e) => setLine(i, { price: e.target.value })} error={parsed[i]!.unitCents === null ? " " : undefined} />
                <button className="mb-2 px-1 text-muted hover:text-danger" aria-label="Remove line" onClick={() => setLines(lines.filter((_, k) => k !== i))}>
                  ✕
                </button>
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-2">
              <select
                aria-label="Add from labor price list"
                className="rounded-[var(--radius-control)] border border-line bg-white px-3 py-2 text-sm"
                value=""
                onChange={(e) => {
                  const item = labor.data?.find((x) => x.id === e.target.value);
                  if (item) setLines([...lines, { laborItemId: item.id, description: item.name, unit: item.unit, quantity: "1", price: dollars(rates.data?.[item.id] ?? item.priceCents) }]);
                }}
              >
                <option value="">+ Add from price list…</option>
                {labor.data
                  ?.filter((x) => !x.archived)
                  .map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name} ({formatCents(rates.data?.[x.id] ?? x.priceCents)}/{x.unit})
                    </option>
                  ))}
              </select>
              <Button variant="quiet" className="px-3 py-1.5" onClick={() => setLines([...lines, { laborItemId: null, description: "", unit: "EA", quantity: "1", price: "0.00" }])}>
                + Custom line
              </Button>
              <Button variant="quiet" className="px-3 py-1.5" busy={fill.isPending} onClick={() => fill.mutate()}>
                Fill from measurement report
              </Button>
              <span className="ml-auto text-sm font-semibold">{priced ? `Total ${formatCents(priced.totalCents)}` : bad !== -1 ? `Fix line ${bad + 1}` : ""}</span>
            </div>
            {fill.error && <p className="text-sm text-danger">{errorMessage(fill.error)}</p>}
            {fill.data && <p className="text-xs text-muted">From {fill.data.from.fileName} ({fill.data.from.wastePercent}% waste){subId ? ", at this sub's rates where they have them" : ""}. Check the quantities.</p>}
          </fieldset>
        )}
      </div>
    </Dialog>
  );
}

function WorkOrderDialog({ id, onClose, onEdit }: { id: string; onClose: () => void; onEdit: (w: WorkOrderDetail) => void }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["work-order", id], queryFn: () => get<WorkOrderDetail>(`/api/work-orders/${id}`) });
  const [link, setLink] = useState<WorkOrderDetail | null>(null);
  const [sending, setSending] = useState(false);
  const done = async (w: WorkOrderDetail) => {
    qc.setQueryData(["work-order", id], w);
    await qc.invalidateQueries({ queryKey: ["work-orders"] });
    await qc.invalidateQueries({ queryKey: ["appointments"] });
  };
  const act = useMutation({
    mutationFn: (what: "new-link" | "accept-time" | "complete" | "cancel") => post<WorkOrderDetail>(`/api/work-orders/${id}/${what}`),
    onSuccess: async (w) => {
      await done(w);
      if (w.subUrl) setLink(w);
    },
  });
  const remove = useMutation({
    mutationFn: () => del(`/api/work-orders/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["work-orders"] });
      onClose();
    },
  });
  const w = q.data;
  const edit = can("workorders.edit");

  if (link) return <SubLinkDialog w={link} onClose={() => setLink(null)} />;
  if (sending && w)
    return (
      <SendDialog
        w={w}
        onClose={() => setSending(false)}
        onSent={async (sent) => {
          setSending(false);
          await done(sent);
          setLink(sent);
        }}
      />
    );

  return (
    <Dialog
      wide
      title={w ? `Work order ${w.number}` : "Work order"}
      onClose={onClose}
      footer={
        w &&
        edit && (
          <div className="flex w-full flex-wrap items-center gap-2">
            {(w.status === "draft" || w.status === "canceled") && (
              <Button variant="quiet" className="text-danger" busy={remove.isPending} onClick={() => window.confirm("Delete this work order?") && remove.mutate()}>
                Delete
              </Button>
            )}
            {!["draft", "completed", "canceled"].includes(w.status) && (
              <Button variant="quiet" busy={act.isPending && act.variables === "cancel"} onClick={() => window.confirm("Cancel this work order? The sub's link stops working.") && act.mutate("cancel")}>
                Cancel work order
              </Button>
            )}
            <span className="flex-1" />
            {w.status !== "completed" && w.status !== "canceled" && <Button onClick={() => onEdit(w)}>Edit</Button>}
            {["sent", "confirmed", "proposed"].includes(w.status) && (
              <Button busy={act.isPending && act.variables === "new-link"} onClick={() => act.mutate("new-link")}>
                Get link
              </Button>
            )}
            {(w.status === "draft" || w.status === "declined") && (
              <Button variant="primary" onClick={() => setSending(true)}>
                {w.status === "declined" ? "Send again" : "Send"}
              </Button>
            )}
            {w.status === "proposed" && (
              <Button variant="primary" busy={act.isPending && act.variables === "accept-time"} onClick={() => act.mutate("accept-time")}>
                Accept new time
              </Button>
            )}
            {w.status === "confirmed" && (
              <Button variant="primary" busy={act.isPending && act.variables === "complete"} onClick={() => act.mutate("complete")}>
                Mark done
              </Button>
            )}
          </div>
        )
      }
    >
      {q.isLoading || !w ? (
        <Loading />
      ) : (
        <div className="flex flex-col gap-4 text-sm">
          {(act.error || remove.error) && <Alert>{errorMessage(act.error ?? remove.error)}</Alert>}
          <div className="flex flex-wrap items-center gap-2">
            <WorkOrderStatusBadge w={w} />
            <Link to={`/jobs/${w.jobId}`} className="text-ink-soft hover:text-brand" onClick={onClose}>
              Job {w.jobLabel}
            </Link>
          </div>
          {w.status === "proposed" && (
            <Alert tone="info">
              {w.sub?.name} proposed {formatDateTime(w.proposedStartsAt)}
              {w.proposedEndsAt && ` – ${formatDateTime(w.proposedEndsAt)}`}.{w.responseNote && ` "${w.responseNote}"`}
            </Alert>
          )}
          {w.status === "declined" && <Alert>{w.sub?.name} declined{w.responseNote ? `: "${w.responseNote}"` : "."}</Alert>}
          {w.status === "confirmed" && w.responseNote && <Alert tone="ok">{w.sub?.name}: "{w.responseNote}"</Alert>}
          {w.noResponse && <Alert>No answer from {w.sub?.name} since {formatDateTime(w.sentAt)}. Give them a call.</Alert>}
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
            <dt className="text-muted">Sub</dt>
            <dd>
              {w.sub ? (
                <Link to={`/contacts/${w.sub.id}`} className="font-semibold hover:text-brand" onClick={onClose}>
                  {w.sub.name}
                </Link>
              ) : (
                "Not picked yet"
              )}
              {w.sub?.phone && <span className="ml-2 text-muted">{w.sub.phone}</span>}
            </dd>
            <dt className="text-muted">When</dt>
            <dd>{when(w)}</dd>
            <dt className="text-muted">Where</dt>
            <dd>{w.address || "—"}</dd>
            {w.sentAt && (
              <>
                <dt className="text-muted">Sent</dt>
                <dd>{formatDateTime(w.sentAt)}</dd>
              </>
            )}
          </dl>
          {w.compliance && !w.compliance.ok && <Alert tone="info">Paperwork: {w.compliance.problems.join("; ")}.</Alert>}
          {w.complianceOverride && (
            <p className="text-muted">
              Sent without complete paperwork by {w.complianceOverride.by}: "{w.complianceOverride.reason}"
            </p>
          )}
          {w.title && <h3 className="text-base font-semibold">{w.title}</h3>}
          {w.scope && (
            <div>
              <div className="font-semibold text-ink-soft">Scope of work</div>
              <RichText text={w.scope} />
            </div>
          )}
          {w.materials && (
            <div>
              <div className="font-semibold text-ink-soft">Materials</div>
              <RichText text={w.materials} />
            </div>
          )}
          {w.lines.length > 0 && <LinesTable lines={w.lines} totalCents={w.totalCents ?? 0} />}
          {w.pdfUrl && (
            <a href={w.pdfUrl} target="_blank" rel="noreferrer" className="font-semibold text-brand hover:underline">
              Open the PDF that was sent
            </a>
          )}
        </div>
      )}
    </Dialog>
  );
}

export function LinesTable({ lines, totalCents }: { lines: WorkOrderDetail["lines"]; totalCents: number }) {
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-xs text-muted uppercase">
        <tr>
          <th className="py-1 font-semibold">Labor</th>
          <th className="py-1 text-right font-semibold">Qty</th>
          <th className="py-1 text-right font-semibold">Each</th>
          <th className="py-1 text-right font-semibold">Amount</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l, i) => (
          <tr key={i} className="border-t border-mist">
            <td className="py-1.5">{l.description}</td>
            <td className="py-1.5 text-right whitespace-nowrap">
              {formatQuantity(l.quantity)} {l.unit}
            </td>
            <td className="py-1.5 text-right">{formatCents(l.unitCents)}</td>
            <td className="py-1.5 text-right">{formatCents(l.totalCents)}</td>
          </tr>
        ))}
        <tr className="border-t border-line font-semibold">
          <td className="py-1.5" colSpan={3}>
            Total
          </td>
          <td className="py-1.5 text-right">{formatCents(totalCents)}</td>
        </tr>
      </tbody>
    </table>
  );
}

/** Sending: missing paperwork asks for a reason first (logged). */
function SendDialog({ w, onClose, onSent }: { w: WorkOrderDetail; onClose: () => void; onSent: (w: WorkOrderDetail) => void }) {
  const problems = w.compliance && !w.compliance.ok ? w.compliance.problems : [];
  const [reason, setReason] = useState("");
  const send = useMutation({ mutationFn: () => post<WorkOrderDetail>(`/api/work-orders/${w.id}/send`, problems.length ? { overrideReason: reason } : {}), onSuccess: onSent });
  const serverProblems = send.error instanceof ApiError && send.error.code === "compliance" ? ((send.error.details as { problems?: string[] })?.problems ?? []) : [];
  const shown = problems.length ? problems : serverProblems;
  return (
    <Dialog
      title={`Send work order ${w.number}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={send.isPending} disabled={shown.length > 0 && reason.trim().length < 3} onClick={() => send.mutate()}>
            {shown.length ? "Send anyway" : "Make PDF and link"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 text-sm">
        {send.error && !serverProblems.length && <Alert>{errorMessage(send.error)}</Alert>}
        <p className="text-ink-soft">This saves the work order as a PDF in the job's files and makes a link for {w.sub?.name ?? "the sub"} to confirm, decline, or propose a new time. You send them the link.</p>
        {shown.length > 0 && (
          <>
            <Alert>
              {w.sub?.name}'s paperwork isn't complete: {shown.join("; ")}.
            </Alert>
            <TextAreaField label="Why send it anyway? (saved in the activity log)" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
          </>
        )}
      </div>
    </Dialog>
  );
}

/** The sub's link, shown once after sending (it isn't stored). Text or email it from the CRM, or copy it. */
function SubLinkDialog({ w, onClose }: { w: WorkOrderDetail; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const url = w.subUrl!;
  return (
    <Dialog
      title="Work order ready"
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-ink-soft">Send it to {w.sub?.name}: Text it or Email it sends from the CRM (you see and can change the message first), or copy the link. The link can't be shown again after this window (you can get a new one later).</p>
        <input readOnly value={url} onFocus={(e) => e.target.select()} className="w-full rounded-[var(--radius-control)] border border-line bg-mist px-3 py-2 text-sm" />
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={async () => {
              await navigator.clipboard.writeText(url);
              setCopied(true);
            }}
          >
            {copied ? "Copied" : "Copy link"}
          </Button>
          {w.sub?.phone && <TextItButton kind="work_order" id={w.id} link={url} jobId={w.jobId} />}
          {w.sub?.email && <EmailItButton kind="work_order" id={w.id} link={url} jobId={w.jobId} />}
        </div>
      </div>
    </Dialog>
  );
}

// ── Subcontractor contact page ───────────────────────────────────────────

const DOC_TONE: Record<DocState, "ok" | "wait" | "off" | "brand"> = { ok: "ok", expiring: "wait", expired: "brand", missing: "brand", waived: "off" };
const DOC_LABEL: Record<DocState, string> = { ok: "Current", expiring: "Expiring soon", expired: "Expired", missing: "Missing", waived: "Waived" };

/** Paperwork and work orders on a subcontractor's contact page. */
export function SubcontractorPanels({ contactId }: { contactId: string }) {
  const [open, setOpen] = useState<string | null>(null);
  const list = useQuery({ queryKey: ["work-orders", "sub", contactId], queryFn: () => get<WorkOrderDetail[]>(`/api/contacts/${contactId}/work-orders`) });
  return (
    <>
      <CompliancePanel contactId={contactId} />
      <PortalPanel contactId={contactId} />
      <RatesPanel contactId={contactId} />
      <Panel>
        <PanelHeader title="Work orders" />
        {list.isLoading ? (
          <div className="px-5">
            <Loading />
          </div>
        ) : !list.data?.length ? (
          <Empty>No work orders yet.</Empty>
        ) : (
          <WorkOrderRows rows={list.data} showJob onOpen={(w) => setOpen(w.id)} />
        )}
      </Panel>
      {open && <WorkOrderDialog id={open} onClose={() => setOpen(null)} onEdit={() => setOpen(null)} />}
    </>
  );
}

function CompliancePanel({ contactId }: { contactId: string }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["compliance", contactId], queryFn: () => get<ComplianceDetail>(`/api/contacts/${contactId}/compliance`) });
  const [editing, setEditing] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const c = q.data;
  return (
    <Panel>
      <PanelHeader
        title="Paperwork"
        description="Workers' comp, general liability, and W-9. Missing or expired paperwork needs a reason to send a work order."
        action={
          can("customers.edit") &&
          c && (
            <div className="flex gap-2">
              {(["expiring", "expired", "missing"].includes(c.workersComp.state) || ["expiring", "expired", "missing"].includes(c.generalLiability.state)) && (
                <Button className="px-3 py-1.5" onClick={() => (setSaid(null), setReminding(true))}>
                  Remind…
                </Button>
              )}
              <Button className="px-3 py-1.5" onClick={() => setEditing(true)}>
                Update
              </Button>
            </div>
          )
        }
      />
      {said && (
        <div className="px-5 pt-3">
          <Alert tone="ok">{said}</Alert>
        </div>
      )}
      {reminding && <RemindDialog contactId={contactId} name="this sub" onClose={() => setReminding(false)} onSent={(m) => (setReminding(false), setSaid(m))} />}
      {q.isLoading || !c ? (
        <div className="px-5">
          <Loading />
        </div>
      ) : (
        <ul className="text-sm">
          {(
            [
              [c.wcKind === "exemption" ? "Workers' comp exemption (State of Oklahoma)" : "Workers' comp", c.workersComp],
              ["General liability", c.generalLiability],
              ["W-9", c.w9],
            ] as [string, ComplianceDoc][]
          ).map(([label, d]) => (
            <li key={label} className="flex flex-wrap items-center gap-3 border-b border-mist px-5 py-3">
              <span className="font-semibold">{label}</span>
              <Badge tone={DOC_TONE[d.state]}>{DOC_LABEL[d.state]}</Badge>
              {d.expiresOn && d.state !== "waived" && <span className="text-muted">expires {formatDate(d.expiresOn)}</span>}
              {label === "W-9" && !d.fileId && c.w9OnFile && d.state !== "waived" && <span className="text-muted">on file in JobNimbus; tag it from their documents</span>}
              {d.url && (
                <a href={d.url} target="_blank" rel="noreferrer" className="ml-auto text-brand hover:underline">
                  {d.fileName}
                </a>
              )}
            </li>
          ))}
          <li className="flex flex-wrap items-center gap-3 px-5 py-3">
            <span className="font-semibold">Tax ID</span>
            <span>{c.taxId ?? c.taxIdMasked ?? "Not on file"}</span>
            <span className="text-muted">{c.is1099 ? "Gets a 1099" : "No 1099"}</span>
          </li>
        </ul>
      )}
      {editing && c && (
        <ComplianceDialog
          contactId={contactId}
          c={c}
          onClose={() => setEditing(false)}
          onSaved={async (d) => {
            qc.setQueryData(["compliance", contactId], d);
            await qc.invalidateQueries({ queryKey: ["files", "contact", contactId] });
            await qc.invalidateQueries({ queryKey: ["compliance"], exact: true });
            setEditing(false);
          }}
        />
      )}
    </Panel>
  );
}

/** Uploads a document to the subcontractor's files and returns its id. */
async function uploadDoc(contactId: string, f: File) {
  const contentType = f.type || "application/octet-stream";
  const [slot] = await post<{ original: { key: string; url: string; headers: Record<string, string> } }[]>(`/api/contacts/${contactId}/files/upload-urls`, { files: [{ name: f.name, contentType, size: f.size }] });
  const res = await fetch(slot!.original.url, { method: "PUT", headers: slot!.original.headers, body: f });
  if (!res.ok) throw new Error("Upload failed. Try again.");
  return post<{ id: string }>(`/api/contacts/${contactId}/files`, { key: slot!.original.key, name: f.name, contentType, size: f.size, category: "Insurance" });
}

type DocChoice = { file?: File; fileId?: string } | null;

/** A certificate: upload a new one, or pick one already in their documents. */
function DocPicker({ label, doc, docs, onPick }: { label: string; doc: ComplianceDoc; docs: FileItem[]; onPick: (c: DocChoice) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<string | null>(null);
  return (
    <div>
      <div className="mb-1 text-sm font-semibold text-ink-soft">{label}</div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className={cx(!picked && !doc.fileName && "text-muted")}>{picked ?? doc.fileName ?? "No file"}</span>
        <input
          ref={input}
          type="file"
          accept="application/pdf,image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            setPicked(f?.name ?? null);
            onPick(f ? { file: f } : null);
          }}
        />
        <Button variant="quiet" className="px-3 py-1" onClick={() => input.current?.click()}>
          Upload
        </Button>
        {docs.length > 0 && (
          <select
            aria-label={`${label}: pick from their documents`}
            className="max-w-full rounded-[var(--radius-control)] border border-line px-2 py-1"
            value=""
            onChange={(e) => {
              const f = docs.find((x) => x.id === e.target.value);
              if (!f) return;
              setPicked(f.name);
              onPick({ fileId: f.id });
            }}
          >
            <option value="">Pick from their documents…</option>
            {docs.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name} ({formatDate(f.createdAt.slice(0, 10))})
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}

function ComplianceDialog({ contactId, c, onClose, onSaved }: { contactId: string; c: ComplianceDetail; onClose: () => void; onSaved: (d: ComplianceDetail) => void }) {
  const [wcKind, setWcKind] = useState<"policy" | "exemption" | "">(c.wcKind ?? "");
  const [wcExpires, setWcExpires] = useState(c.workersComp.expiresOn ?? "");
  const [glExpires, setGlExpires] = useState(c.generalLiability.expiresOn ?? "");
  const [files, setFiles] = useState<{ wc?: DocChoice; gl?: DocChoice; w9?: DocChoice }>({});
  const [taxId, setTaxId] = useState("");
  const [is1099, setIs1099] = useState(c.is1099);
  const [wcWaived, setWcWaived] = useState(c.wcWaived);
  const [glWaived, setGlWaived] = useState(c.glWaived);
  const [w9Waived, setW9Waived] = useState(c.w9Waived);
  const docs = useQuery({ queryKey: ["files", "contact", contactId], queryFn: () => get<FileItem[]>(`/api/contacts/${contactId}/files`) });
  const theirDocs = (docs.data ?? []).filter((f) => !f.isPhoto);
  const save = useMutation({
    mutationFn: async () => {
      const ids: Record<string, string> = {};
      for (const k of ["wc", "gl", "w9"] as const) {
        const f = files[k];
        if (f?.file) ids[k] = (await uploadDoc(contactId, f.file)).id;
        else if (f?.fileId) ids[k] = f.fileId;
      }
      return put<ComplianceDetail>(`/api/contacts/${contactId}/compliance`, {
        wcKind: wcKind || null,
        wcExpiresOn: wcExpires || null,
        glExpiresOn: glExpires || null,
        is1099,
        wcWaived,
        glWaived,
        w9Waived,
        ...(ids.wc ? { wcFileId: ids.wc } : {}),
        ...(ids.gl ? { glFileId: ids.gl } : {}),
        ...(ids.w9 ? { w9FileId: ids.w9 } : {}),
        ...(taxId.trim() ? { taxId } : {}),
      });
    },
    onSuccess: onSaved,
  });
  return (
    <Dialog
      title="Update paperwork"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <SelectField label="Workers' comp" value={wcKind} onChange={(e) => setWcKind(e.target.value as typeof wcKind)}>
          <option value="">Not on file</option>
          <option value="policy">Workers' comp policy</option>
          <option value="exemption">State of Oklahoma exemption</option>
        </SelectField>
        {wcKind && (
          <div className="grid gap-3 sm:grid-cols-2">
            <DocPicker label="Certificate" doc={c.workersComp} docs={theirDocs} onPick={(f) => setFiles({ ...files, wc: f })} />
            <Field label="Expires" type="date" value={wcExpires} onChange={(e) => setWcExpires(e.target.value)} />
          </div>
        )}
        <Checkbox label="Workers' comp waived (none needed)" checked={wcWaived} onChange={setWcWaived} />
        <div className="grid gap-3 sm:grid-cols-2">
          <DocPicker label="General liability certificate" doc={c.generalLiability} docs={theirDocs} onPick={(f) => setFiles({ ...files, gl: f })} />
          <Field label="Expires" type="date" value={glExpires} onChange={(e) => setGlExpires(e.target.value)} />
        </div>
        <Checkbox label="General liability waived (none needed)" checked={glWaived} onChange={setGlWaived} />
        <DocPicker label="W-9" doc={c.w9} docs={theirDocs} onPick={(f) => setFiles({ ...files, w9: f })} />
        <Checkbox label="W-9 waived" checked={w9Waived} onChange={setW9Waived} />
        <Field label="Tax ID (SSN or EIN)" value={taxId} onChange={(e) => setTaxId(e.target.value)} placeholder={c.taxIdMasked ?? "9 digits"} autoComplete="off" hint={c.taxIdMasked ? "Leave blank to keep the one on file. It's stored encrypted." : "Stored encrypted. Most people see only the last four digits."} />
        <Checkbox label="Gets a 1099" checked={is1099} onChange={setIs1099} />
      </div>
    </Dialog>
  );
}


/** The sub's portal link: all their work orders in one place, and uploads (photos, invoices, certificates). */
function PortalPanel({ contactId }: { contactId: string }) {
  const { can } = useSession();
  const [url, setUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const make = useMutation({ mutationFn: () => post<{ url: string }>(`/api/contacts/${contactId}/portal-link`), onSuccess: (r) => (setUrl(r.url), setCopied(false)) });
  const off = useMutation({ mutationFn: () => del(`/api/contacts/${contactId}/portal-link`), onSuccess: () => setUrl(null) });
  if (!can("workorders.edit")) return null;
  return (
    <Panel>
      <PanelHeader
        title="Sub portal"
        description="One link for this sub to see all their work orders, confirm times, and send photos, invoices, and new certificates. Making a new link turns off the old one."
        action={
          <div className="flex gap-2">
            <Button className="px-3 py-1.5" busy={make.isPending} onClick={() => make.mutate()}>
              New portal link
            </Button>
            <Button variant="quiet" className="px-3 py-1.5" busy={off.isPending} onClick={() => window.confirm("Turn off this sub's portal link?") && off.mutate()}>
              Turn off
            </Button>
          </div>
        }
      />
      {(make.error || off.error) && (
        <div className="px-5 pt-4">
          <Alert>{errorMessage(make.error ?? off.error)}</Alert>
        </div>
      )}
      {url && (
        <div className="flex flex-col gap-2 px-5 py-4">
          <p className="text-sm text-ink-soft">Send this to the sub. Copy it now: it can't be shown again.</p>
          <div className="flex gap-2">
            <input readOnly value={url} onFocus={(e) => e.target.select()} className="w-full rounded-[var(--radius-control)] border border-line bg-mist px-3 py-2 text-sm" />
            <Button onClick={async () => (await navigator.clipboard.writeText(url), setCopied(true))}>{copied ? "Copied" : "Copy"}</Button>
          </div>
        </div>
      )}
    </Panel>
  );
}

/** What this sub charges, where it isn't the labor price list's price. */
function RatesPanel({ contactId }: { contactId: string }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const labor = useLaborItems();
  const rates = useQuery({ queryKey: ["labor-rates", contactId], queryFn: () => get<Record<string, number>>(`/api/contacts/${contactId}/labor-rates`), enabled: can("money.view") });
  const [draft, setDraft] = useState<Record<string, string> | null>(null);
  const save = useMutation({
    mutationFn: () =>
      put<Record<string, number>>(`/api/contacts/${contactId}/labor-rates`, {
        rates: Object.entries(draft ?? {}).map(([laborItemId, v]) => ({ laborItemId, priceCents: v.trim() === "" ? null : Math.round(Number(v) * 100) })),
      }),
    onSuccess: (r) => (qc.setQueryData(["labor-rates", contactId], r), setDraft(null)),
  });
  if (!can("money.view") || !labor.data?.length) return null;
  const items = labor.data.filter((x) => !x.archived);
  const value = (id: string) => draft?.[id] ?? (rates.data?.[id] !== undefined ? (rates.data[id]! / 100).toFixed(2) : "");
  const bad = Object.values(draft ?? {}).some((v) => v.trim() !== "" && !/^\d+(\.\d{1,2})?$/.test(v.trim()));
  return (
    <Panel>
      <PanelHeader title="Their labor rates" description="Leave blank to use the labor price list. Their rates fill in on their work orders." />
      <ul>
        {items.map((x) => (
          <li key={x.id} className="grid grid-cols-[1fr_120px_110px] items-center gap-3 border-b border-mist px-5 py-2 text-sm">
            <span>{x.name}</span>
            <span className="text-muted">list {formatCents(x.priceCents)}/{x.unit}</span>
            <input
              aria-label={`${x.name} rate`}
              inputMode="decimal"
              disabled={!can("workorders.edit")}
              className="rounded-[var(--radius-control)] border border-line px-2 py-1"
              placeholder={(x.priceCents / 100).toFixed(2)}
              value={value(x.id)}
              onChange={(e) => setDraft({ ...(draft ?? {}), [x.id]: e.target.value })}
            />
          </li>
        ))}
      </ul>
      {can("workorders.edit") && draft && (
        <div className="flex items-center gap-3 px-5 py-3">
          <Button variant="primary" busy={save.isPending} disabled={bad} onClick={() => save.mutate()}>
            Save rates
          </Button>
          {save.error && <span className="text-sm text-danger">{errorMessage(save.error)}</span>}
        </div>
      )}
    </Panel>
  );
}

/**
 * A sub's documents (their contact page's files) with the paperwork tags: "W-9", "Workers' comp",
 * "General liability" on the current ones, and "Use as…" on each to make it one. An older certificate
 * stays a regular document.
 */
export function SubDocuments({ contactId, readOnly }: { contactId: string; readOnly?: boolean }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["compliance", contactId], queryFn: () => get<ComplianceDetail>(`/api/contacts/${contactId}/compliance`) });
  const [tagging, setTagging] = useState<FileItem | null>(null);
  const c = q.data;
  const badges: Record<string, string[]> = {};
  const add = (id: string | null, label: string) => id && (badges[id] = [...(badges[id] ?? []), label]);
  if (c) {
    add(c.workersComp.fileId, c.wcKind === "exemption" ? "WC exemption" : "Workers' comp");
    add(c.generalLiability.fileId, "General liability");
    add(c.w9.fileId, "W-9");
  }
  return (
    <>
      <FilesPanel
        owner={{ kind: "contact", id: contactId }}
        readOnly={readOnly}
        badges={badges}
        docAction={(f) =>
          c && can("customers.edit") ? (
            <button className="shrink-0 rounded px-2 py-1 text-sm text-brand hover:bg-mist" onClick={() => setTagging(f)}>
              Use as…
            </button>
          ) : null
        }
      />
      {tagging && c && (
        <TagDocDialog
          contactId={contactId}
          file={tagging}
          c={c}
          onClose={() => setTagging(null)}
          onSaved={(d) => {
            qc.setQueryData(["compliance", contactId], d);
            setTagging(null);
          }}
        />
      )}
    </>
  );
}

/** Tags one of a sub's documents as their W-9, workers' comp, or liability certificate (one certificate often covers both). */
function TagDocDialog({ contactId, file, c, onClose, onSaved }: { contactId: string; file: FileItem; c: ComplianceDetail; onClose: () => void; onSaved: (d: ComplianceDetail) => void }) {
  const [wc, setWc] = useState(c.workersComp.fileId === file.id);
  const [gl, setGl] = useState(c.generalLiability.fileId === file.id);
  const [w9, setW9] = useState(c.w9.fileId === file.id);
  const [wcKind, setWcKind] = useState<"policy" | "exemption">(c.wcKind ?? "policy");
  const [wcExpires, setWcExpires] = useState(c.workersComp.fileId === file.id ? (c.workersComp.expiresOn ?? "") : "");
  const [glExpires, setGlExpires] = useState(c.generalLiability.fileId === file.id ? (c.generalLiability.expiresOn ?? "") : "");
  const missing = (wc && !wcExpires) || (gl && !glExpires);
  const save = useMutation({
    mutationFn: () =>
      put<ComplianceDetail>(`/api/contacts/${contactId}/compliance`, {
        ...(wc ? { wcKind, wcFileId: file.id, wcExpiresOn: wcExpires } : {}),
        ...(gl ? { glFileId: file.id, glExpiresOn: glExpires } : {}),
        ...(w9 ? { w9FileId: file.id } : {}),
      }),
    onSuccess: onSaved,
  });
  return (
    <Dialog
      title={`Use ${file.name} as…`}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={(!wc && !gl && !w9) || missing} busy={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <p className="text-sm text-ink-soft">It stays in their documents. Whatever it replaces stays there too, as a regular document.</p>
        <Checkbox label="Workers' comp" checked={wc} onChange={setWc} />
        {wc && (
          <div className="grid gap-3 pl-7 sm:grid-cols-2">
            <SelectField label="Kind" value={wcKind} onChange={(e) => setWcKind(e.target.value as typeof wcKind)}>
              <option value="policy">Workers' comp policy</option>
              <option value="exemption">State of Oklahoma exemption</option>
            </SelectField>
            <Field label="Expires" type="date" value={wcExpires} onChange={(e) => setWcExpires(e.target.value)} />
          </div>
        )}
        <Checkbox label="General liability" checked={gl} onChange={setGl} />
        {gl && (
          <div className="grid gap-3 pl-7 sm:grid-cols-2">
            <Field label="Expires" type="date" value={glExpires} onChange={(e) => setGlExpires(e.target.value)} />
          </div>
        )}
        <Checkbox label="W-9" checked={w9} onChange={setW9} />
      </div>
    </Dialog>
  );
}
